import { type SetURLSearchParams } from 'react-router'
import maxBy from 'lodash/maxBy'
import { clearCartRestoreParam, restoreCart, saveCart } from './userCartService'
import { FLOWS } from './userCart.consts'
import { getCoverPreviewUrl } from './coverPreviewUrl'
import { deserializeCover, deserializePages } from '../PhotoBook/photoBookDTO'

import { analytics, EVENT_NAMES } from '../Analytics/Analytics'
import { UPLOAD_SRC } from '../../components/PhotoBank/PhotoBank.consts'
import { getPhotoBankStore, getPhotoBooksStore } from 'stores/RootStoreProvider'
import {
  type SerializedPhoto,
  type SerializedPhotoBook,
} from '../PhotoBook/photoBookDTO.types'
import { LEGACY_PHOTO_BOOK_ID } from '../PhotoBook/photoBookId'
import { type StoredPhotoBook } from '../PhotoBook/photoBookSaver.types'
import { type RawRemotePhoto } from 'components/PhotoBank/PhotoBank.types'
import { readMobxSnapshot } from 'utils/mobx/readOutsideReaction'

export const saveBooksToUserCart = async (books: SerializedPhotoBook[]) => {
  // Every book rides in cartItems; the template params show a single cover —
  // the freshest book's — because the email template is single-cover today.
  const mostRecentBook = maxBy(books, (book) => book.lastEditedAt ?? 0)
  await saveCart({
    flow: FLOWS.PHOTO_BOOKS,
    cartItems: books,
    photoBookCoverUrl:
      mostRecentBook && getCoverPreviewUrl(mostRecentBook.cover),
    emailTemplateParams: mostRecentBook
      ? { 'Cover Caption': mostRecentBook.cover.caption?.text }
      : {},
  })
}

export const updateBooksFromUserCart = async (
  setSearchParams: SetURLSearchParams
) => {
  const { cartId, userCart } = await restoreCart(FLOWS.PHOTO_BOOKS)
  if (userCart) {
    clearCartRestoreParam(FLOWS.PHOTO_BOOKS, setSearchParams)
  }
  if (!userCart || userCart.cartItems.length === 0) {
    return
  }

  let restoredBooksCount = 0
  for (const cartItem of userCart.cartItems) {
    if (await restoreUserCartBook(cartItem)) {
      restoredBooksCount++
    }
  }

  analytics.track(EVENT_NAMES.CART_RESTORED, {
    Flow: FLOWS.PHOTO_BOOKS,
    'Restored Photo Books Count': restoredBooksCount,
    'Restored Cart ID': cartId,
  })
}

export async function restoreUserCartBook(
  bookData: SerializedPhotoBook
): Promise<StoredPhotoBook | null> {
  const book = deserializeUserCartBook(bookData)
  if (!book) {
    return null
  }
  await getPhotoBooksStore().receiveExternalBook(book, 'User Cart')
  return book
}

export function deserializeUserCartBook(
  bookData: SerializedPhotoBook
): StoredPhotoBook | null {
  const {
    bookId,
    lastEditedAt = 0,
    sortKey,
    cover,
    pages,
    bookSize,
    quantity = 1,
    aiGeneratedCovers,
  } = bookData
  if (!cover.photo && !cover.designedCoverId && !cover.aiGeneratedCoverUrl) {
    return null
  }
  const pagePhotos = pages.map((page) => page.photos[0] as SerializedPhoto)
  const photos: RawRemotePhoto[] = [
    ...(cover.photo ? [cover.photo] : []),
    ...pagePhotos,
  ].map(({ url, width, height, uploadedAt }) => {
    return {
      uid: `${url}-${uploadedAt}`,
      originalUrl: url,
      width,
      height,
      uploadedAt,
      uploadSrc: UPLOAD_SRC.PHOTO_BOOK,
      wasUsed: true,
      hashCode: url,
    }
  })

  const store = getPhotoBankStore()
  store.appendPhotos({ photos, uploadSrc: UPLOAD_SRC.PHOTO_BOOK })
  const photoBank = readMobxSnapshot(() => store.remotePhotos)
  const deserializedCover = deserializeCover(cover, photoBank)
  const deserializedPages = deserializePages(pages, photoBank)
  if (!deserializedCover) {
    return null
  }

  return {
    bookId: bookId ?? LEGACY_PHOTO_BOOK_ID,
    lastEditedAt,
    sortKey: sortKey ?? lastEditedAt,
    cover: deserializedCover,
    pages: deserializedPages,
    size: bookSize,
    quantity,
    aiGeneratedCovers: aiGeneratedCovers ?? [],
  }
}
